/**
 * Mailchimp Marketing API client for the case lookup page.
 *
 * Every call goes through the dev-server proxy in src/setupProxy.js, which adds
 * the Basic auth header and picks the right datacentre host. Nothing here works
 * in a production build, by design - see the comments in that file.
 *
 * All requests are GETs. This module cannot change anything in the Mailchimp
 * account, which matters when the output is going into a legal file.
 */

const PROXY_BASE = "/mailchimp-api"

/** Attempts (including the first) before a call is treated as failed */
const MAX_ATTEMPTS = 4

/** Mailchimp allows 10 simultaneous connections per key; stay well under it */
export const MAILCHIMP_CONCURRENCY = 4

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Perform a single GET against the proxy.
 *
 * Retries 429s (rate limit) and 5xx responses with exponential backoff. A 404
 * is returned as `{ notFound: true }` rather than thrown, because "this address
 * is not in this audience" is a normal, expected answer.
 */
const request = async (path, apiKey, { allowNotFound = false } = {}) => {
	let lastError = null

	for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
		let response

		try {
			response = await fetch(`${PROXY_BASE}${path}`, {
				method: "GET",
				headers: { "x-mailchimp-key": apiKey },
			})
		} catch (error) {
			// The dev server itself is unreachable - almost always "not running"
			throw new Error("Could not reach the local Mailchimp proxy. Is the app running via npm start?")
		}

		if (response.status === 404 && allowNotFound) {
			return { notFound: true }
		}

		if (response.status === 429 || response.status >= 500) {
			lastError = new Error(`Mailchimp returned ${response.status}`)
			if (attempt < MAX_ATTEMPTS) {
				await sleep(500 * Math.pow(2, attempt - 1))
				continue
			}
			throw lastError
		}

		const text = await response.text()
		let payload = null

		try {
			payload = text ? JSON.parse(text) : {}
		} catch (error) {
			throw new Error(`Mailchimp returned a non-JSON response (${response.status})`)
		}

		if (!response.ok) {
			throw new Error(payload?.detail || payload?.error || `Mailchimp returned ${response.status}`)
		}

		return payload
	}

	throw lastError || new Error("Mailchimp request failed")
}

/**
 * Confirm the key works and report which account it belongs to. Called once
 * when the key is entered so the page can show a connected state rather than
 * failing on the first address.
 */
export const verifyKey = async (apiKey) => {
	const [ping, account] = await Promise.all([
		request("/ping", apiKey),
		request("/?fields=account_name,email,total_subscribers", apiKey),
	])

	return {
		health: ping?.health_status || "",
		accountName: account?.account_name || "",
		accountEmail: account?.email || "",
		totalSubscribers: account?.total_subscribers ?? null,
	}
}

/**
 * Every audience (list) on the account. Fetched once per run and reused, both
 * to name audiences in the report and to know what an "everything" search
 * actually covered.
 */
export const fetchAudiences = async (apiKey) => {
	const payload = await request("/lists?count=1000&fields=lists.id,lists.name,lists.date_created,lists.stats.member_count,lists.stats.unsubscribe_count,lists.stats.cleaned_count,total_items", apiKey)

	return (payload?.lists || []).map((list) => ({
		id: list.id,
		name: list.name,
		created: list.date_created || "",
		memberCount: list.stats?.member_count ?? null,
		unsubscribeCount: list.stats?.unsubscribe_count ?? null,
		cleanedCount: list.stats?.cleaned_count ?? null,
	}))
}

/**
 * Fields we want back for any member record, whether it came from a search or
 * from paging an audience's archived contacts.
 */
const MEMBER_FIELDS = [
	"id",
	"email_address",
	"status",
	"unsubscribe_reason",
	"timestamp_signup",
	"timestamp_opt",
	"last_changed",
	"member_rating",
	"source",
	"ip_signup",
	"ip_opt",
	"language",
	"vip",
	"list_id",
	"contact_id",
	"tags",
	"merge_fields",
	"stats",
	"consents_to_one_to_one_messaging",
]

/**
 * Normalise a raw Mailchimp member object into the shape the rest of the page
 * works with, so search results and archived results are interchangeable.
 */
const normaliseMember = (member, listId) => ({
	subscriberHash: member.id || "",
	email: member.email_address || "",
	listId: member.list_id || listId || "",
	status: member.status || "",
	unsubscribeReason: member.unsubscribe_reason || "",
	signedUp: member.timestamp_signup || "",
	optedIn: member.timestamp_opt || "",
	lastChanged: member.last_changed || "",
	rating: member.member_rating ?? null,
	source: member.source || "",
	signupIP: member.ip_signup || "",
	optInIP: member.ip_opt || "",
	language: member.language || "",
	vip: Boolean(member.vip),
	contactID: member.contact_id || "",
	tags: (member.tags || []).map((tag) => tag.name).filter(Boolean),
	firstName: member.merge_fields?.FNAME || "",
	lastName: member.merge_fields?.LNAME || "",
	phone: member.merge_fields?.PHONE || "",
	openRate: member.stats?.avg_open_rate ?? null,
	clickRate: member.stats?.avg_click_rate ?? null,
})

/**
 * Page through one audience's archived contacts.
 *
 * Archived contacts are excluded from /search-members, so without this pass an
 * address that was archived would be reported as "never on any mailing list",
 * which would be wrong. Built once per run and reused for every address, since
 * paging per address would be far more expensive.
 */
export const fetchArchivedIndex = async (apiKey, audiences, onProgress) => {
	const index = new Map()
	const pageSize = 1000

	for (const audience of audiences) {
		let offset = 0
		let total = null

		do {
			const fields = `total_items,${MEMBER_FIELDS.map((field) => `members.${field}`).join(",")}`
			const payload = await request(`/lists/${audience.id}/members?status=archived&count=${pageSize}&offset=${offset}&fields=${fields}`, apiKey)

			total = payload?.total_items ?? 0

			for (const member of payload?.members || []) {
				const key = (member.email_address || "").toLowerCase()
				if (!key) continue

				const existing = index.get(key) || []
				existing.push({ ...normaliseMember(member, audience.id), archived: true })
				index.set(key, existing)
			}

			offset += pageSize

			if (onProgress) {
				onProgress({ audience: audience.name, scanned: Math.min(offset, total), total })
			}
		} while (offset < total)
	}

	return index
}

/**
 * Search every audience for one address.
 *
 * `exact_matches` is what we report as a membership. `full_search` is Mailchimp's
 * fuzzy pass (it also matches names and merge fields); those are kept separately
 * as "possible related contacts" and never counted as the subject's own record.
 */
export const searchMember = async (apiKey, email) => {
	const fields = [
		...MEMBER_FIELDS.map((field) => `exact_matches.members.${field}`),
		"exact_matches.total_items",
		"full_search.members.id",
		"full_search.members.email_address",
		"full_search.members.status",
		"full_search.members.list_id",
		"full_search.total_items",
	].join(",")

	const payload = await request(`/search-members?query=${encodeURIComponent(email)}&fields=${fields}`, apiKey)

	const exact = (payload?.exact_matches?.members || []).map((member) => normaliseMember(member))
	const related = (payload?.full_search?.members || [])
		// Drop anything that is the address itself - it is already an exact match
		.filter((member) => (member.email_address || "").toLowerCase() !== email.toLowerCase())
		.map((member) => ({
			email: member.email_address || "",
			status: member.status || "",
			listId: member.list_id || "",
			subscriberHash: member.id || "",
		}))

	return { exact, related }
}

/**
 * The most recent events Mailchimp holds for a member in one audience: sends,
 * opens, clicks, bounces, unsubscribes and signups.
 *
 * Mailchimp caps this endpoint at the last 50 events per member, which is a hard
 * API limit and is stated in the report's method notes.
 */
export const fetchMemberActivity = async (apiKey, listId, subscriberHash) => {
	const payload = await request(`/lists/${listId}/members/${subscriberHash}/activity?count=50`, apiKey, { allowNotFound: true })

	if (payload?.notFound) return []

	return (payload?.activity || []).map((event) => ({
		listId,
		action: event.action || "",
		timestamp: event.timestamp || "",
		type: event.type || "",
		campaignID: event.campaign_id || "",
		title: event.title || "",
		url: event.url || "",
		ip: event.ip || "",
	}))
}

/**
 * Any tags applied to the member, with the date each was added. The member
 * payload carries tag names but not when they were applied, and "when were they
 * added to a list" is one of the questions being asked.
 */
export const fetchMemberTags = async (apiKey, listId, subscriberHash) => {
	const payload = await request(`/lists/${listId}/members/${subscriberHash}/tags?count=500&fields=tags.name,tags.date_added`, apiKey, { allowNotFound: true })

	if (payload?.notFound) return []

	return (payload?.tags || []).map((tag) => ({
		name: tag.name || "",
		added: tag.date_added || "",
	}))
}

/**
 * Campaign detail, cached across the whole run. Member activity gives a campaign
 * id and sometimes a title; this turns those ids into the campaign name, subject
 * line and send date that a reader of the report can actually recognise.
 */
export const makeCampaignResolver = (apiKey) => {
	const cache = new Map()

	return async (campaignID) => {
		if (!campaignID) return null
		if (cache.has(campaignID)) return cache.get(campaignID)

		let campaign = null

		try {
			const payload = await request(`/campaigns/${campaignID}?fields=id,type,status,send_time,emails_sent,settings.title,settings.subject_line,settings.from_name,archive_url,recipients.list_id`, apiKey, { allowNotFound: true })

			if (!payload?.notFound) {
				campaign = {
					id: payload?.id || campaignID,
					type: payload?.type || "",
					status: payload?.status || "",
					sendTime: payload?.send_time || "",
					emailsSent: payload?.emails_sent ?? null,
					title: payload?.settings?.title || "",
					subject: payload?.settings?.subject_line || "",
					fromName: payload?.settings?.from_name || "",
					archiveURL: payload?.archive_url || "",
					listId: payload?.recipients?.list_id || "",
				}
			}
		} catch (error) {
			// A campaign we cannot read must not sink the whole address; the
			// activity row still carries the campaign id
			campaign = null
		}

		cache.set(campaignID, campaign)
		return campaign
	}
}

/**
 * Everything Mailchimp holds for one address, across every audience.
 */
export const lookupMailchimp = async (apiKey, email, { audiences, archivedIndex, resolveCampaign }) => {
	const audienceNames = new Map(audiences.map((audience) => [audience.id, audience.name]))

	const { exact, related } = await searchMember(apiKey, email)

	// Fold in any archived memberships the search could not see
	const archived = archivedIndex?.get(email.toLowerCase()) || []
	const seen = new Set(exact.map((member) => `${member.listId}:${member.subscriberHash}`))
	const memberships = [...exact]

	for (const member of archived) {
		const key = `${member.listId}:${member.subscriberHash}`
		if (seen.has(key)) continue
		seen.add(key)
		memberships.push(member)
	}

	// Activity and tag dates for each membership found
	const activity = []

	for (const membership of memberships) {
		membership.audienceName = audienceNames.get(membership.listId) || membership.listId

		const [events, tags] = await Promise.all([
			fetchMemberActivity(apiKey, membership.listId, membership.subscriberHash),
			fetchMemberTags(apiKey, membership.listId, membership.subscriberHash),
		])

		membership.tagDetail = tags

		for (const event of events) {
			const campaign = await resolveCampaign(event.campaignID)

			activity.push({
				...event,
				audienceName: audienceNames.get(event.listId) || event.listId,
				campaignTitle: campaign?.title || event.title || "",
				campaignSubject: campaign?.subject || "",
				campaignSendTime: campaign?.sendTime || "",
			})
		}
	}

	// Newest first - reads better in both the PDF and the spreadsheet
	activity.sort((a, b) => String(b.timestamp).localeCompare(String(a.timestamp)))

	return {
		memberships,
		activity,
		related,
		audiencesSearched: audiences.length,
	}
}
